/**
 * Bulk Form Copy (Super Admin) — copy the selected forms ONE PER REQUEST.
 *
 * WHY: every sendAction goes through API Gateway, which cuts a request off at 29s and answers
 * with a CORS-less 504 that axios reports as "Network Error". On 2026-10-02 a 2-form copy
 * (Summerbrook → Brester) took 64s server-side: the forms WERE created, but the page showed
 * "Network Error". A copy's time grows with the number of forms (and each cross-site form runs an
 * AI auto-assign), so one request for the whole selection can never be made safe. One form per
 * request keeps each call well under the cap and gives the operator a progress counter.
 *
 * Outcomes per form:
 *   - answered          → the backend's own successCount/failureCount/skippedCount + details
 *   - no answer (network error / 502 / 503 / 504) → 'unknown': the server may still have
 *     created it. Counted separately, NEVER as a failure, and the loop carries on — forms are
 *     independent, and a retry without "Overwrite" only skips what already exists.
 *   - refused (any other non-200, e.g. an incomplete imaging map) → the same refusal would hit
 *     every form, so the run STOPS and the rest are reported as not attempted.
 *
 * Keep this file IMPORT-FREE: cypress/node-tests/bulk-form-copy-runner.test.js executes it.
 */

/** HTTP statuses where the gateway/Lambda gave no real answer — the copy may or may not have run. */
export const NO_ANSWER_STATUSES = [502, 503, 504];

export const UNKNOWN_OUTCOME_REASON =
    'No answer from the server in time — the copy may still have finished. ' +
    "Reload the target site's forms before retrying (a retry without Overwrite skips forms that already exist).";

/**
 * Did this sendAction result come back without a real answer from the backend?
 * `superadmin/sendAction` never throws: on a network error it resolves
 * `{ status: 500, data: { message: err.message, executionSummary: { status: 'ERROR' } } }`.
 */
export function isNoAnswer(response) {
    if (!response) return true;
    if (NO_ANSWER_STATUSES.indexOf(response.status) > -1) return true;
    const data = response.data;
    if (response.status === 500 && data && typeof data === 'object' && typeof data.message === 'string'
        && /network error|timeout|timed out/i.test(data.message)
        && data.executionSummary && data.executionSummary.status === 'ERROR') {
        return true;
    }
    return false;
}

/** A 200 whose body is the bulkCopyForms results object. */
export function isAnswered(response) {
    return !!(response && response.status === 200 && response.data && typeof response.data === 'object'
        && !Array.isArray(response.data));
}

/**
 * @param {Object} opts
 * @param {string[]} opts.formIDs           selection, snapshotted by the caller
 * @param {Function} opts.send              async (formID) => sendAction result ({ status, data })
 * @param {Function} [opts.nameOf]          (formID) => display name
 * @param {Function} [opts.describeError]   (response) => message for a refusal
 * @param {Function} [opts.onProgress]      ({ done, total, formID, name }) before each form
 * @returns {Promise<{successCount, failureCount, skippedCount, unknownCount, notAttemptedCount, details, stoppedReason}>}
 */
export async function runBulkFormCopy({ formIDs, send, nameOf, describeError, onProgress }) {
    const ids = Array.isArray(formIDs) ? formIDs.slice() : [];
    const name = (id) => (typeof nameOf === 'function' && nameOf(id)) || id;
    const results = {
        successCount: 0,
        failureCount: 0,
        skippedCount: 0,
        unknownCount: 0,
        notAttemptedCount: 0,
        details: [],
        stoppedReason: null,
    };

    for (let i = 0; i < ids.length; i++) {
        const formID = ids[i];
        if (typeof onProgress === 'function') onProgress({ done: i, total: ids.length, formID, name: name(formID) });

        let response;
        try {
            response = await send(formID);
        } catch (err) {
            response = { status: 500, data: { message: (err && err.message) || 'Network Error', executionSummary: { status: 'ERROR' } } };
        }

        if (isAnswered(response)) {
            const data = response.data;
            results.successCount += Number(data.successCount) || 0;
            results.failureCount += Number(data.failureCount) || 0;
            results.skippedCount += Number(data.skippedCount) || 0;
            const details = Array.isArray(data.details) ? data.details : [];
            if (details.length > 0) {
                details.forEach(d => results.details.push(d));
            } else {
                // A results object with no detail row would make the form vanish from the table.
                results.details.push({ formID, formName: name(formID), status: 'unknown', reason: 'The server answered without a result for this form' });
                results.unknownCount++;
            }
            continue;
        }

        if (isNoAnswer(response)) {
            results.unknownCount++;
            results.details.push({ formID, formName: name(formID), status: 'unknown', reason: UNKNOWN_OUTCOME_REASON });
            continue;
        }

        // Refused: the same refusal would hit every remaining form — stop here.
        const message = (typeof describeError === 'function' && describeError(response)) || `Request failed with status ${response.status}`;
        results.failureCount++;
        results.details.push({ formID, formName: name(formID), status: 'failed', reason: message });
        results.stoppedReason = message;
        for (let j = i + 1; j < ids.length; j++) {
            results.notAttemptedCount++;
            results.details.push({ formID: ids[j], formName: name(ids[j]), status: 'not attempted', reason: 'Stopped after the server refused an earlier form' });
        }
        break;
    }

    if (typeof onProgress === 'function') onProgress({ done: ids.length, total: ids.length, formID: null, name: null });
    return results;
}
